import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const EmptyWishlistState = () => {
  const navigate = useNavigate();

  const handleStartAnalysis = () => {
    navigate('/photo-upload');
  };

  const handleBrowseRecommendations = () => {
    navigate('/upgrade-recommendations');
  };

  const handleViewDashboard = () => {
    navigate('/user-dashboard');
  };

  return (
    <div className="text-center py-12 px-6">
      {/* Illustration */}
      <div className="mb-8">
        <div className="w-32 h-32 mx-auto bg-muted/50 rounded-full flex items-center justify-center mb-6">
          <Icon name="Heart" size={48} color="var(--color-muted-foreground)" />
        </div>
        
        <div className="max-w-md mx-auto">
          <Image
            src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop"
            alt="Empty wishlist illustration"
            className="w-full h-48 object-cover rounded-lg opacity-60"
          />
        </div>
      </div>

      {/* Content */}
      <div className="max-w-lg mx-auto mb-8">
        <h2 className="text-2xl font-bold text-foreground mb-4">
          Your Wishlist is Empty
        </h2>
        <p className="text-muted-foreground mb-6 leading-relaxed">
          Start building your dream bike setup! Upload a photo of your current bike to get personalized upgrade recommendations, or browse our curated suggestions to find components that match your riding style.
        </p>
      </div>

      {/* Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-8">
        <div className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-all duration-200">
          <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Camera" size={24} color="var(--color-accent)" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">
            Analyze Your Bike
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Upload a photo and get AI-powered recommendations tailored to your specific bike and riding needs.
          </p>
          <Button
            variant="default"
            size="sm"
            onClick={handleStartAnalysis}
            iconName="Upload"
            iconPosition="left"
            fullWidth
          >
            Start Analysis
          </Button>
        </div>

        <div className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-all duration-200">
          <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Zap" size={24} color="var(--color-accent)" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">
            Browse Recommendations
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Explore curated upgrade suggestions based on popular bike types and riding styles.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={handleBrowseRecommendations}
            iconName="Search"
            iconPosition="left"
            fullWidth
          >
            Browse Upgrades
          </Button>
        </div>

        <div className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-all duration-200">
          <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="BarChart3" size={24} color="var(--color-accent)" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">
            View Dashboard
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Check your bike analysis history and track your upgrade journey over time.
          </p>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleViewDashboard}
            iconName="LayoutDashboard"
            iconPosition="left"
            fullWidth
          >
            Go to Dashboard
          </Button>
        </div>
      </div>

      {/* Tips */}
      <div className="bg-muted/50 rounded-lg p-6 max-w-2xl mx-auto">
        <h4 className="text-lg font-semibold text-foreground mb-3 flex items-center justify-center space-x-2">
          <Icon name="Lightbulb" size={20} />
          <span>Pro Tips</span>
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-muted-foreground">
          <div className="flex items-start space-x-2">
            <Icon name="CheckCircle" size={16} className="mt-0.5 flex-shrink-0" color="var(--color-success)" />
            <span>Save items from any recommendation page by clicking the heart icon</span>
          </div>
          <div className="flex items-start space-x-2">
            <Icon name="CheckCircle" size={16} className="mt-0.5 flex-shrink-0" color="var(--color-success)" />
            <span>Set price alerts to get notified when items go on sale</span>
          </div>
          <div className="flex items-start space-x-2">
            <Icon name="CheckCircle" size={16} className="mt-0.5 flex-shrink-0" color="var(--color-success)" />
            <span>Organize items by priority to plan your upgrade timeline</span>
          </div>
          <div className="flex items-start space-x-2">
            <Icon name="CheckCircle" size={16} className="mt-0.5 flex-shrink-0" color="var(--color-success)" />
            <span>Add personal notes to remember why you want each upgrade</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmptyWishlistState;